// Salary / compensation (payroll-service, via the gateway). Salary is
// effective-dated: each revision supersedes the last and keeps history. Setting
// a salary requires the employee to be assigned to payroll (a snapshot) first.
import axios from 'axios';
import { attachCookieAuth } from '@/lib/session';

const BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const api = axios.create({ baseURL: BASE, timeout: 20000, withCredentials: true });
api.interceptors.request.use(attachCookieAuth);

export interface Compensation {
  amount: string;
  currency: string;
  effective_from: string;
  effective_to?: string | null;
  active?: boolean;
}

export interface LegalEntityLite { id: string; name: string }

/** Salary history (newest first). 404 = the employee isn't on payroll yet.
 *  The API returns `{ compensation: [...] }`; tolerate a bare array too. */
export const getCompensation = (employeeId: string): Promise<Compensation[]> =>
  api
    .get<{ compensation?: Compensation[] } | Compensation[]>(
      `/api/v1/payroll/employees/${employeeId}/compensation`,
    )
    .then((r) => (Array.isArray(r.data) ? r.data : r.data?.compensation ?? []));

export const setCompensation = (
  employeeId: string,
  body: { amount: string; currency?: string; effective_from: string },
) =>
  api
    .post(`/api/v1/payroll/employees/${employeeId}/compensation`, { currency: 'TZS', ...body })
    .then((r) => r.data);

/** Put an employee on payroll (creates the snapshot needed before setting pay). */
export const assignToPayroll = (
  employeeId: string,
  body: { legal_entity_id: string; full_name?: string; email?: string; national_id?: string },
) => api.post(`/api/v1/payroll/employees/${employeeId}/assignment`, body).then((r) => r.data);

export const listLegalEntities = () =>
  api
    .get<{ legal_entities: LegalEntityLite[] }>('/api/v1/legal-entities')
    .then((r) => r.data.legal_entities || []);
